import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthUtils } from 'app/session/auth/auth.utils';
import { ProfileService } from 'app/modules/profile/profile.service';
import { FuseConfigService } from '@fuse/services/config';
import { environment } from 'environments/environment';
import { apiMessageOptions } from 'app/shared/interceptors/api-message-context';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;
    private _unauthorizedHandled: boolean = false;
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: ProfileService,
        private _fuseConfigService: FuseConfigService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('access_token', token);
    }

    get accessToken(): string {
        return localStorage.getItem('access_token') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string, password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            console.error('[AuthService] Sign-in blocked: user is already marked as authenticated.');
            return throwError(() => new Error('User is already logged in.'));
        }

        return this._httpClient.post(
            `${environment.api_url}/api/session/sign-in`,
            credentials,
            apiMessageOptions(false)
        )
            .pipe(
                switchMap((response: any) => {
                    const payload = this._applySessionResponse(response, true);

                    if (!payload?.token || !payload?.user) {
                        console.error('[AuthService] Sign-in response is missing expected fields.', {
                            hasToken: !!payload?.token,
                            hasUser: !!payload?.user,
                            response
                        });
                    }

                    return of(payload || response);
                }),
                catchError((error) => {
                    console.error('[AuthService] Sign-in request failed.', {
                        endpoint: `${environment.api_url}/api/session/sign-in`,
                        error
                    });
                    return throwError(() => error);
                })
            );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient.post(
            `${environment.api_url}/api/session/refresh-token`,
            {},
            apiMessageOptions(false)
        ).pipe(
            catchError((error) => {
                console.error('[AuthService] Token refresh failed.', {
                    endpoint: `${environment.api_url}/api/session/refresh-token`,
                    error
                });

                localStorage.removeItem('access_token');
                localStorage.removeItem('impersonator_access_token');
                this._authenticated = false;

                // Return false
                return of(false);
            }),
            switchMap((response: any) => {
                const payload = this._extractPayload(response);

                if (!payload || typeof payload !== 'object' || !payload.token) {
                    console.error('[AuthService] Token refresh returned an invalid payload.', {
                        response
                    });
                    return of(false);
                }

                this._applySessionResponse(response, true);

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Forgot password
     *
     * @param user
     */
    forgotPassword(user: { email: string }): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/session/forget-password`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Activate account
     *
     * @param user
     */
    activateAccount(user: { email: string }): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/session/activate-account`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Validate OTP
     *
     * @param user
     */
    validateOTP(user: { email: string, code: string, reference: string, action: string }): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/dashboard/validate-otp`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Reset password
     *
     * @param user  
     */
    resetPassword(user: { email: string, reference: string, code: string, password_new: string, password_confirm: string }): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/session/reset-password`,
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        return this._httpClient.post(
            `${environment.api_url}/api/session/sign-out`,
            {},
            apiMessageOptions(false)
        ).pipe(
            catchError(() => {

                // Continue logout flow even if API sign-out fails
                return of(true);
            }),
            switchMap(() => {

                // Remove session data from local storage
                localStorage.removeItem('access_token');
                localStorage.removeItem('impersonator_access_token');
                localStorage.removeItem('selected-organization');
                localStorage.removeItem('selected-branch');
                localStorage.removeItem('app-config');
                this._userService.selectedOrganization = null;
                this._userService.selectedBranch = null;

                // Set the authenticated flag to false
                this._authenticated = false;

                // Return the observable
                return of(true);
            })
        );
    }

    impersonate(email: string): Observable<any> {
        const wasAlreadyImpersonating = this.isImpersonating();
        const targetEmail = `${email || ''}`.trim();

        if (!targetEmail) {
            return throwError(() => new Error('Target user email is required for impersonation.'));
        }

        if (!localStorage.getItem('impersonator_access_token') && this.accessToken) {
            localStorage.setItem('impersonator_access_token', this.accessToken);
        }

        return this._httpClient.post(
            `${environment.api_url}/api/session/impersonate`,
            { email: targetEmail },
            apiMessageOptions(false)
        ).pipe(
            switchMap((response: any) => {
                // Save current selections only on the first impersonation
                if (!wasAlreadyImpersonating) {
                    const currentOrg = localStorage.getItem('selected-organization');
                    const currentBranch = localStorage.getItem('selected-branch');
                    if (currentOrg) {
                        localStorage.setItem('impersonator_selected_organization', currentOrg);
                    } else {
                        localStorage.removeItem('impersonator_selected_organization');
                    }
                    if (currentBranch) {
                        localStorage.setItem('impersonator_selected_branch', currentBranch);
                    } else {
                        localStorage.removeItem('impersonator_selected_branch');
                    }
                }

                // Skip immediate user/org broadcasts here and let the dashboard bootstrapping reload clean state.
                const payload = this._applySessionResponse(response, false, false);
                return of(payload || response);
            }),
            catchError((error) => {
                if (!this.isImpersonating()) {
                    localStorage.removeItem('impersonator_access_token');
                }
                return throwError(() => error);
            })
        );
    }

    unimpersonate(): Observable<boolean> {
        const originalToken = localStorage.getItem('impersonator_access_token');
        if (!originalToken) {
            return of(false);
        }

        this.accessToken = originalToken;

        return this.signInUsingToken().pipe(
            map((ok) => {
                if (ok) {
                    localStorage.removeItem('impersonator_access_token');

                    // Restore original selections
                    const savedOrg = localStorage.getItem('impersonator_selected_organization');
                    const savedBranch = localStorage.getItem('impersonator_selected_branch');
                    try {
                        this._userService.selectedOrganization = savedOrg ? JSON.parse(savedOrg) : null;
                    } catch (e) {
                        this._userService.selectedOrganization = null;
                    }
                    try {
                        this._userService.selectedBranch = savedBranch ? JSON.parse(savedBranch) : null;
                    } catch (e) {
                        this._userService.selectedBranch = null;
                    }
                    localStorage.removeItem('impersonator_selected_organization');
                    localStorage.removeItem('impersonator_selected_branch');

                    return true;
                }
                return false;
            }),
            catchError(() => of(false))
        );
    }

    isImpersonating(): boolean {
        return !!localStorage.getItem('impersonator_access_token');
    }

    private _restoreSavedWorkspaceSelection(organizations: any[], user: any = null): void {
        const savedOrganization = this._parseStoredSelection('selected-organization');
        const savedBranch = this._parseStoredSelection('selected-branch');

        const matchedOrganization = savedOrganization?.code
            ? organizations.find((organization: any) => organization?.code === savedOrganization.code) || null
            : null;

        const matchedBranch = matchedOrganization && savedBranch?.code
            ? (matchedOrganization?.branches || []).find((branch: any) => branch?.code === savedBranch.code) || null
            : null;

        if (matchedOrganization && matchedBranch) {
            this._userService.selectedOrganization = matchedOrganization;
            this._userService.selectedBranch = matchedBranch;

            const normalizedUser = this._normalizeUser(user, organizations, matchedOrganization, matchedBranch);
            if (normalizedUser) {
                this._userService.user = normalizedUser;
            }

            if (matchedOrganization?.config) {
                this._fuseConfigService.applyPermanentLayoutChanges(matchedOrganization.config);
            }
            return;
        }

        localStorage.removeItem('app-config');
        this._userService.selectedOrganization = null;
        this._userService.selectedBranch = null;
    }

    private _applySessionResponse(response: any, preserveSavedSelection: boolean, emitState: boolean = true): any {
        const payload = this._extractPayload(response);
        const token = `${payload?.token || ''}`;
        const organizations = this._normalizeOrganizations(payload?.organizations);
        const normalizedUser = this._normalizeUser(payload?.user, organizations);

        if (token) {
            this.accessToken = token;
            this._authenticated = true;
        }

        if (emitState && normalizedUser) {
            this._userService.user = normalizedUser;
        }

        if (emitState) {
            this._userService.organizations = organizations;
        }

        if (emitState && preserveSavedSelection) {
            this._restoreSavedWorkspaceSelection(organizations, normalizedUser);
        } else {
            localStorage.removeItem('app-config');
            this._userService.selectedOrganization = null;
            this._userService.selectedBranch = null;
        }

        return {
            ...payload,
            user: normalizedUser,
            organizations,
            token
        };
    }

    private _extractPayload(response: any): any {
        return response?.payload && typeof response.payload === 'object'
            ? response.payload
            : response;
    }

    private _normalizeOrganizations(organizations: any): any[] {
        if (Array.isArray(organizations)) {
            return organizations.map((organization: any) => this._normalizeOrganization(organization));
        }

        if (organizations && typeof organizations === 'object') {
            return Object.values(organizations).map((organization: any) => this._normalizeOrganization(organization));
        }

        return [];
    }

    private _normalizeOrganization(organization: any): any {
        const branches = Array.isArray(organization?.branches)
            ? organization.branches
            : organization?.branches && typeof organization.branches === 'object'
                ? Object.values(organization.branches)
                : [];

        return {
            ...organization,
            branches
        };
    }

    private _normalizeUser(user: any, organizations: any[], selectedOrganization: any = null, selectedBranch: any = null): any {
        if (!user) {
            return null;
        }

        const roleMap = this._extractRoleMap(user?.role) || this._extractRoleMap(user?.extra?.role_map);
        const resolvedRoles = this._resolveEffectiveRoles(roleMap || user?.role, organizations, selectedOrganization, selectedBranch, user);

        return {
            ...user,
            role: resolvedRoles,
            extra: {
                ...(user?.extra || {}),
                role_map: roleMap || user?.extra?.role_map || null
            }
        };
    }

    private _extractRoleMap(value: any): Record<string, any> | null {
        return value && typeof value === 'object' && !Array.isArray(value)
            ? value as Record<string, any>
            : null;
    }

    private _resolveEffectiveRoles(roleSource: any, organizations: any[] = [], selectedOrganization: any = null, selectedBranch: any = null, user: any = null): string[] {
        const roleMap = this._extractRoleMap(roleSource);
        const fallbackOrganizationCode = selectedOrganization?.code
            || organizations?.[0]?.code
            || Object.keys(roleMap || {})[0]
            || null;

        return AuthUtils.resolveEffectiveRoles(roleSource, {
            selectedOrganizationCode: selectedOrganization?.code || null,
            selectedBranchCode: selectedBranch?.code || null,
            fallbackOrganizationCode,
            user
        });
    }

    private _parseStoredSelection(storageKey: string): any | null {
        const raw = localStorage.getItem(storageKey);

        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw);
        } catch (error) {
            localStorage.removeItem(storageKey);
            return null;
        }
    }

    // handleUnauthorized(error: any, requestUrl: string): void {
    //     if (this._unauthorizedHandled) {
    //         return;
    //     }

    //     this._unauthorizedHandled = true;

    //     console.error('[AuthService] Unauthorized response detected. Clearing session and redirecting to sign-in.', {
    //         requestUrl,
    //         error
    //     });

    //     localStorage.removeItem('access_token');
    //     localStorage.removeItem('impersonator_access_token');
    //     this._authenticated = false;

    //     setTimeout(() => {
    //         this._unauthorizedHandled = false;
    //     }, 3000);

    //     window.location.assign('/sign-in');
    // }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string, email: string, password: string, company: string }): Observable<any> {
        return this._httpClient.post(
            'api/auth/sign-up',
            user,
            apiMessageOptions(false)
        );
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string, password: string }): Observable<any> {
        return this._httpClient.post(
            'api/auth/unlock-session',
            credentials,
            apiMessageOptions(false)
        );
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
